import { useTranslation } from "react-i18next";
import { Parcel } from "@/mocks/parcels";
import StatusBadge from "./StatusBadge";

interface ParcelInfoCardProps {
  parcel: Parcel;
}

interface InfoRowProps {
  icon: string;
  label: string;
  value: string;
  mono?: boolean;
  accent?: boolean;
}

function InfoRow({ icon, label, value, mono, accent }: InfoRowProps) {
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-50 last:border-0">
      <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${accent ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
        <i className={`${icon} text-sm`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 font-medium mb-0.5">{label}</p>
        <p className={`text-sm text-slate-700 font-semibold break-words ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
      </div>
    </div>
  );
}

export default function ParcelInfoCard({ parcel }: ParcelInfoCardProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-2xl border border-emerald-100 overflow-hidden">
      {/* Card header */}
      <div className="px-5 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-between">
        <div>
          <p className="text-emerald-100 text-xs font-medium mb-0.5">{t("parcel_info_title")}</p>
          <p className="text-white font-bold font-mono text-base tracking-wide">{parcel.id}</p>
        </div>
        <StatusBadge status={parcel.status} />
      </div>

      {/* Delivery section */}
      <div className="px-5 pt-4 pb-1">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t("tracking_delivery_details")}</p>
        <InfoRow icon="ri-user-3-line" label={t("parcel_recipient")} value={parcel.recipient} accent />
        <InfoRow icon="ri-phone-line" label={t("parcel_phone")} value={parcel.phone} accent />
        <InfoRow icon="ri-home-4-line" label={t("parcel_delivery_address")} value={parcel.deliveryAddress} accent />
      </div>

      {/* Divider */}
      <div className="mx-5 border-t border-dashed border-slate-200 my-1" />

      {/* Shipment section */}
      <div className="px-5 pt-1 pb-4">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1 mt-2">{t("tracking_shipment_info")}</p>
        <InfoRow icon="ri-user-line" label={t("parcel_sender")} value={parcel.sender} />
        <InfoRow icon="ri-map-pin-line" label={t("parcel_origin")} value={parcel.origin} />
        <InfoRow icon="ri-flag-line" label={t("parcel_destination")} value={parcel.destination} />
        <InfoRow icon="ri-scales-3-line" label={t("parcel_weight")} value={parcel.weight} />
        <InfoRow icon="ri-box-3-line" label={t("parcel_dimensions")} value={parcel.dimensions} />
        <InfoRow icon="ri-calendar-check-line" label={t("parcel_estimated_delivery")} value={parcel.estimatedDelivery} accent />
      </div>
    </div>
  );
}
